import { Component, ChangeDetectorRef, forwardRef, inject } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export type DaySchedule = { open: boolean; from: string; to: string };
export type WorkingHours = Record<DayKey, DaySchedule>;

export const DEFAULT_WORKING_HOURS: WorkingHours = {
  mon: { open: true,  from: '09:00', to: '18:00' },
  tue: { open: true,  from: '09:00', to: '18:00' },
  wed: { open: true,  from: '09:00', to: '18:00' },
  thu: { open: true,  from: '09:00', to: '18:00' },
  fri: { open: true,  from: '09:00', to: '18:00' },
  sat: { open: false, from: '09:00', to: '18:00' },
  sun: { open: false, from: '09:00', to: '18:00' },
};

const WEEKDAYS: { key: DayKey; label: string }[] = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
];

const WEEKEND: { key: DayKey; label: string }[] = [
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

// ─── Component ────────────────────────────────────────────────────────────────

@Component({
  selector: 'op-working-hours-input',
  standalone: true,
  imports: [MatSlideToggleModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WorkingHoursInputComponent),
      multi: true,
    },
  ],
  templateUrl: './working-hours-input.component.html',
  styleUrl: './working-hours-input.component.scss',
})
export class WorkingHoursInputComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);

  readonly WEEKDAYS = WEEKDAYS;
  readonly WEEKEND  = WEEKEND;

  hours: WorkingHours = structuredClone(DEFAULT_WORKING_HOURS);
  disabled = false;

  private onChange: (v: WorkingHours) => void = () => {};
  private onTouched: () => void = () => {};

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(val: WorkingHours | null): void {
    this.hours = val ? structuredClone(val) : structuredClone(DEFAULT_WORKING_HOURS);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (v: WorkingHours) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  setDisabledState(d: boolean): void { this.disabled = d; }

  // ── Event handlers ────────────────────────────────────────────────────────

  toggleOpen(key: DayKey, open: boolean): void {
    this.hours = { ...this.hours, [key]: { ...this.hours[key], open } };
    this.onChange(this.hours);
    this.onTouched();
  }

  setTime(key: DayKey, field: 'from' | 'to', event: Event): void {
    const val = (event.target as HTMLInputElement).value;
    if (!val) return;
    this.hours = { ...this.hours, [key]: { ...this.hours[key], [field]: val } };
    this.onChange(this.hours);
    this.onTouched();
  }
}